/**
 *
 * BIGTINCAN - CONFIDENTIAL
 *
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of BigTinCan Mobile Pty Ltd and its suppliers,
 * if any. The intellectual and technical concepts contained herein are proprietary to BigTinCan Mobile Pty Ltd and its
 * suppliers and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or
 * copyright law. Dissemination of this information or reproduction of this material is strictly forbidden unless prior
 * written permission is obtained from BigTinCan Mobile Pty Ltd.
 *
 * @package app.bigtincan.dsr
 * @copyright 2010-2020 BigTinCan Mobile Pty Ltd
 * @author Shibu Bhattarai <shibu.bhattarai@bigtincan.com>
 */

import axios, { AxiosRequestConfig, AxiosInstance, AxiosResponse } from 'axios';
import { Result } from '../model';
import envConfig from '../config';
import { getNewConfig, authenticationInterceptor } from './interceptor';

export interface RequestOptions {
  url: string;
  data?: any;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
}

export interface SendFormDataOptions {
  url: string;
  data: FormData;
  method: 'POST' | 'PUT' | 'PATCH';
}

/**
 * Represents base class of the Api Request.
 */
export abstract class ApiBase {
  httpClient: AxiosInstance;

  /**
   *
   */
  constructor() {
    const url = envConfig.apiBase;
    this.httpClient = axios.create({
      baseURL: url,
    });
    this.httpClient.interceptors.request.use(getNewConfig);
    this.httpClient.interceptors.response.use((response: AxiosResponse) => response,
      error => authenticationInterceptor(this.httpClient, error));
  }

  /**
   * Make request Get Request
   */
  public async doGet<T>(
    url: string,
    config: AxiosRequestConfig = {},
  ): Promise<Result<T>> {
    let axiosResult = null;
    let result = null;

    const axiosRequestConfig: AxiosRequestConfig = config;
    try {
      axiosResult = await this.httpClient.get(url, axiosRequestConfig);
      result = new Result<T>(axiosResult.data);
    } catch (error) {
      result = new Result(error.response, error.message);
    }

    return result;
  }

  /**
   * Make request POST Request
   */
  public async doPost<T>(
    url: string,
    data?: unknown,
    config: AxiosRequestConfig = {},
  ): Promise<Result<T>> {
    let axiosResult = null;
    let result = null;

    const axiosRequestConfig: AxiosRequestConfig = config;
    try {
      axiosResult = await this.httpClient.post(url, data, axiosRequestConfig);
      result = new Result<T>(axiosResult.data);
    } catch (error) {
      result = new Result(error.response, error.message);
    }

    return result;
  }

  /**
   * Make request PUT Request
   */
  public async doPut<T>(url: string, data: unknown): Promise<Result<T>> {
    let axiosResult = null;
    let result = null;

    const axiosRequestConfig: AxiosRequestConfig = {};
    try {
      axiosResult = await this.httpClient.put(url, data, axiosRequestConfig);
      result = new Result(axiosResult.data);
    } catch (error) {
      result = new Result(error.response, error.message);
    }

    return result;
  }

  /**
   * Make request Patch Request
   */
  public async doPatch<T>(url: string, data: unknown): Promise<Result<T>> {
    let axiosResult = null;
    let result = null;

    const axiosRequestConfig: AxiosRequestConfig = {};
    try {
      axiosResult = await this.httpClient.patch(url, data, axiosRequestConfig);
      result = new Result(axiosResult.data);
    } catch (error) {
      result = new Result(error.response, error.message);
    }

    return result;
  }

  /**
   * Make request Delete Request
   */
  public async doDelete<T>(url: string): Promise<Result<T>> {
    let axiosResult = null;
    let result = null;

    const axiosRequestConfig: AxiosRequestConfig = {};
    try {
      axiosResult = await this.httpClient.delete(url, axiosRequestConfig);
      result = new Result(axiosResult.data);
    } catch (error) {
      result = new Result(error.response, error.message);
    }

    return result;
  }

  public transformRequest = (jsonData = {}) => Object.entries(jsonData)
    .map(
      x => `${encodeURIComponent(x[0])}=${encodeURIComponent(x[1] as string)}`,
    )
    .join('&');
}
